import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import {
	Box,
	Chip,
	Collapse,
	IconButton,
	Link,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import { Controller } from "react-hook-form";

import { TensileRequirementByGrade } from "@/pages/configuration/models/TensileRequirementByGrade";
import { backgroundColor } from "@/utils/theme";
import { getMomentTz } from "@dexteel/mesf-core";
import { HeatMechanicalResult } from "../../models/HeatMechanicalResult";
import { MechanicalTestRunDisposition } from "../../models/MechanicalTestRunDisposition";
import {
	ChangedValue,
	DispositionSelect,
	ResultChip,
	RunHistoryBar,
	SeqLabel,
	SoftChip,
	StatusChip,
} from "./cells";
import { CustomInput } from "./CustomInput";
import { disposedByLabel, formatTimestamp } from "./format";
import { RequirementLimits, VALUE_COLUMNS, changedCellSx } from "./layout";
import {
	MechanicalCurvesChart,
	useHeatMechanicalCurves,
} from "./MechanicalCurvesChart";
import { RunActions } from "./RunActions";
import {
	PLACEHOLDER_RUN_ID,
	RunGroup,
	buildRunGroups,
	overallChangeKey,
	valueChangeKey,
} from "./runGroups";
import { MechanicalRunEdit } from "./types";
import { useRunEditor } from "./useRunEditor";
import { useRunHistory } from "./useRunHistory";

const moment = getMomentTz();

const gridSx = {
	display: "grid",
	gridTemplateColumns: "64px repeat(4, minmax(0, 1fr)) 48px",
	columnGap: 1,
	alignItems: "center",
};

const CARD_PX = 1.25;

const cellSx = {
	alignSelf: "stretch",
	display: "flex",
	alignItems: "center",
	mx: -0.5,
	px: 0.5,
	my: -0.5,
	py: 0.5,
};

const REPORTED_WIDTH = 100;

const renderValue = (formatted: string | number) => {
	const text = String(formatted);
	const limitStart = text.indexOf(" (");
	if (limitStart < 0) return text;

	return (
		<>
			{text.slice(0, limitStart)}
			<Box component="span" sx={{ fontSize: 11, ml: 0.5 }}>
				{text.slice(limitStart + 1)}
			</Box>
		</>
	);
};

const RunComment = ({ comment }: { comment: string }) => {
	const [expanded, setExpanded] = useState(false);
	const isLong = comment.length > 150;

	return (
		<Typography
			variant="caption"
			color="text.secondary"
			component="div"
			sx={{ fontSize: 11 }}
		>
			{isLong && !expanded ? `${comment.slice(0, 150).trimEnd()}…` : comment}
			{isLong && (
				<Link
					component="button"
					underline="hover"
					onClick={() => setExpanded(!expanded)}
					sx={{ fontSize: 11, ml: 0.5, verticalAlign: "baseline" }}
				>
					{expanded ? "See less" : "See more"}
				</Link>
			)}
		</Typography>
	);
};

const seqRange = (results: HeatMechanicalResult[]) => {
	if (results.length === 0) return "No specimens";
	const sequences = results.map((result) => result.SampleSequence);
	const first = Math.min(...sequences);
	const last = Math.max(...sequences);
	return first === last ? `Seq ${first}` : `Seq ${first}-${last}`;
};

const revTooltip = (
	rev: number | null | undefined,
	effectiveDate: Date | string | null | undefined,
) =>
	`Rev ${rev ?? "-"}, effective ${effectiveDate ? moment(effectiveDate).format("MM/DD/YY") : "-"}`;

const LimitsRow = ({
	rev,
	limits,
	tooltip,
	highlight = false,
}: {
	rev: number | null | undefined;
	limits: RequirementLimits | null;
	tooltip: string;
	highlight?: boolean;
}) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			py: 0.5,
			fontSize: 11,
			fontWeight: 600,
			borderTop: 1,
			borderColor: "divider",
			whiteSpace: "nowrap",
			color: highlight ? "warning.dark" : "text.secondary",
			bgcolor: (theme) =>
				highlight
					? alpha(theme.palette.warning.main, 0.12)
					: theme.palette.action.hover,
		}}
	>
		<Box sx={{ ...gridSx, flex: 1, minWidth: 0, px: CARD_PX }}>
			<Tooltip title={tooltip} arrow>
				<Box
					component="span"
					sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
				>
					{highlight && <InfoOutlinedIcon sx={{ fontSize: 13 }} />}
					{rev != null ? `Rev ${rev}` : "Ref"}
				</Box>
			</Tooltip>
			{VALUE_COLUMNS.map((column) => (
				<Box
					key={column.field}
					sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
				>
					{limits ? column.range(limits) : "-"}
				</Box>
			))}
			<Box />
		</Box>
		<Box sx={{ width: REPORTED_WIDTH, flexShrink: 0 }} />
	</Box>
);

type Props = {
	results: HeatMechanicalResult[];
	requirement?: TensileRequirementByGrade | null;
	dispositions?: MechanicalTestRunDisposition[];
	onSaveRun?: (
		mechanicalTestRunId: number,
		edit: MechanicalRunEdit,
	) => Promise<unknown> | void;
	isSaving?: boolean;
	allowEmptyRun?: boolean;
	showSeqColors?: boolean;
	curves?: { heatId: number; heatName?: string | null };
};

export const MechanicalResultsCards = ({
	results,
	requirement = null,
	dispositions = [],
	onSaveRun,
	isSaving = false,
	allowEmptyRun = false,
	showSeqColors = false,
	curves,
}: Props) => {
	const [openCurves, setOpenCurves] = useState<number[]>([]);

	const groups = buildRunGroups(results, dispositions, allowEmptyRun);
	const editor = useRunEditor({ onSaveRun });
	const history = useRunHistory();
	const canEdit = !!onSaveRun;

	const { data: curveRows } = useHeatMechanicalCurves(curves?.heatId);
	const runsWithCurves = new Set(
		(curves ? (curveRows ?? []) : []).map((curve) => curve.MechanicalTestRunId),
	);

	const toggleCurves = (runId: number) =>
		setOpenCurves((open) =>
			open.includes(runId)
				? open.filter((id) => id !== runId)
				: [...open, runId],
		);

	const testedGroups = groups.filter(
		(group) => group.runId !== PLACEHOLDER_RUN_ID,
	);
	const passedGroups = testedGroups.filter(
		(group) => group.disposition?.Disposition === "P",
	).length;
	const specimenCount = groups.reduce(
		(count, group) => count + group.results.length,
		0,
	);

	const renderLimits = (shown: RunGroup) => {
		const runResult = shown.results[0];
		if (!runResult) return null;

		if (runResult.RequirementId == null)
			return (
				<LimitsRow
					rev={requirement?.Rev}
					limits={requirement}
					tooltip={
						requirement
							? revTooltip(requirement.Rev, requirement.EffectiveDate)
							: "No spec for this run"
					}
				/>
			);

		const runTooltip = revTooltip(
			runResult.RequirementRev,
			runResult.RequirementEffectiveDate,
		);

		if (!requirement || runResult.RequirementRev === requirement.Rev)
			return (
				<LimitsRow
					rev={runResult.RequirementRev}
					limits={runResult}
					tooltip={runTooltip}
				/>
			);

		return (
			<>
				<LimitsRow
					rev={runResult.RequirementRev}
					limits={runResult}
					tooltip={`Evaluated against ${runTooltip} - the current spec is Rev ${requirement.Rev ?? "-"}`}
					highlight
				/>
				<LimitsRow
					rev={requirement.Rev}
					limits={requirement}
					tooltip={revTooltip(requirement.Rev, requirement.EffectiveDate)}
				/>
			</>
		);
	};

	return (
		<Stack spacing={1}>
			<Stack direction="row" alignItems="baseline" spacing={1}>
				<Typography
					variant="subtitle2"
					fontWeight={600}
					color="text.secondary"
					sx={{ flex: 1 }}
				>
					Mechanical Tests
				</Typography>
				{testedGroups.length > 0 && (
					<Typography variant="caption" color="text.secondary">
						{`${specimenCount} seq · ${passedGroups}/${testedGroups.length} groups passed`}
					</Typography>
				)}
			</Stack>

			{groups.map((group) => {
				const view = history.viewOf(group);
				const shown = view?.group ?? group;
				const isEditing = editor.isEditing(group.runId);
				const isPast = !!view && !view.isCurrent && !!view.at;
				const dispositionFrom = view?.updatedFields.get("disposition");
				const isAuto =
					shown.disposition?.DisposedBy?.trim().toLowerCase() === "auto";

				return (
					<Box
						key={group.runId}
						sx={{
							border: 1,
							borderColor: "divider",
							borderRadius: 1,
							overflow: "hidden",
							bgcolor: isEditing
								? backgroundColor
								: isPast
									? "action.hover"
									: "background.paper",
						}}
					>
						{view && (
							<Box
								sx={{
									px: CARD_PX,
									bgcolor: "action.hover",
									borderBottom: 1,
									borderColor: "divider",
								}}
							>
								<RunHistoryBar view={view} />
							</Box>
						)}

						<Box sx={{ px: CARD_PX, pt: 0.75, pb: 0.75 }}>
							<Stack direction="row" spacing={1} alignItems="center">
								<Typography sx={{ fontSize: 13, fontWeight: 700 }}>
									{seqRange(shown.results)}
								</Typography>
								{isEditing ? (
									<Controller
										name="disposition"
										control={editor.control}
										render={({ field }) => (
											<DispositionSelect
												value={field.value}
												disabled={isSaving}
												onChange={field.onChange}
												sx={{ width: 110 }}
											/>
										)}
									/>
								) : (
									<Box
										sx={{
											p: "3px",
											mx: "-3px",
											borderRadius: 1,
											...(dispositionFrom !== undefined
												? changedCellSx(
														"disposition",
														shown.disposition?.Disposition,
													)
												: {}),
										}}
									>
										<ChangedValue previous={dispositionFrom}>
											{shown.disposition ? (
												<StatusChip code={shown.disposition.Disposition} />
											) : (
												<Chip
													label="Pending"
													color="warning"
													variant="outlined"
													size="small"
													sx={{
														height: 20,
														fontSize: 11,
														width: 72,
														"& .MuiChip-label": { px: 0 },
													}}
												/>
											)}
										</ChangedValue>
									</Box>
								)}
								{shown.disposition && (
									<SoftChip
										label={isAuto ? "Auto" : disposedByLabel(shown.disposition)}
										title={isAuto ? "Disposed automatically" : "Disposed by"}
									/>
								)}
								{shown.disposition && (
									<SoftChip
										label={formatTimestamp(shown.disposition.DisposedAt)}
										title="Disposed at"
									/>
								)}
								<Box sx={{ flex: 1 }} />
								{runsWithCurves.has(group.runId) && (
									<Tooltip
										title={
											openCurves.includes(group.runId)
												? "Hide tensile curves"
												: "Show tensile curves"
										}
										arrow
									>
										<IconButton
											size="small"
											color={
												openCurves.includes(group.runId) ? "primary" : "default"
											}
											onClick={() => toggleCurves(group.runId)}
										>
											<ShowChartIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								)}
								<RunActions
									group={group}
									editor={editor}
									history={history}
									canEdit={canEdit}
									isSaving={isSaving}
								/>
							</Stack>

							{isEditing ? (
								<TextField
									name={editor.commentField.name}
									onChange={editor.commentField.onChange}
									onBlur={editor.commentField.onBlur}
									inputRef={editor.commentField.ref}
									placeholder="Comment"
									variant="outlined"
									size="small"
									multiline
									rows={2}
									disabled={isSaving}
									autoComplete="off"
									sx={{
										mt: 0.5,
										width: "100%",
										backgroundColor: (theme) => theme.palette.background.paper,
										"& .MuiOutlinedInput-root": { p: "5px 8px" },
									}}
									inputProps={{ style: { fontSize: 11, padding: 0 } }}
								/>
							) : (
								shown.comment && <RunComment comment={shown.comment} />
							)}
						</Box>

						{shown.results.length === 0 ? (
							<Box
								sx={{
									px: CARD_PX,
									py: 0.75,
									fontSize: 12,
									color: "text.secondary",
									borderTop: 1,
									borderColor: "divider",
								}}
							>
								No specimens loaded
							</Box>
						) : (
							<>
								<Box
									sx={{
										display: "flex",
										py: 0.25,
										fontSize: 11,
										color: "text.secondary",
										borderTop: 1,
										borderColor: "divider",
									}}
								>
									<Box sx={{ ...gridSx, flex: 1, minWidth: 0, px: CARD_PX }}>
										<Box>Seq</Box>
										{VALUE_COLUMNS.map((column) => (
											<Box key={column.field}>{column.label}</Box>
										))}
										<Box sx={{ textAlign: "center" }}>Test</Box>
									</Box>
									<Box
										sx={{
											width: REPORTED_WIDTH,
											flexShrink: 0,
											textAlign: "center",
										}}
									>
										Reported
									</Box>
								</Box>
								<Box
									sx={{ display: "flex", borderTop: 1, borderColor: "divider" }}
								>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										{shown.results.map((result, resultIndex) => {
											const overallFrom = view?.updatedFields.get(
												overallChangeKey(result.MechanicalSampleId),
											);

											return (
												<Box
													key={result.HeatMechanicalResultId}
													sx={{
														...gridSx,
														px: CARD_PX,
														py: 0.5,
														minHeight: 30,
														fontSize: 12,
														borderTop: resultIndex > 0 ? 1 : 0,
														borderColor: "divider",
													}}
												>
													<Box>
														<SeqLabel
															sequence={result.SampleSequence}
															showColor={
																showSeqColors || runsWithCurves.has(group.runId)
															}
														/>
													</Box>
													{VALUE_COLUMNS.map((column) => {
														const updatedFrom = view?.updatedFields.get(
															valueChangeKey(
																result.MechanicalSampleId,
																column.field,
															),
														);
														const failed = column.passed(result) === false;
														const value = renderValue(column.format(result));

														return (
															<Box
																key={column.field}
																sx={{
																	...cellSx,
																	color: failed ? "error.main" : undefined,
																	fontWeight: failed ? 600 : undefined,
																	...(updatedFrom !== undefined
																		? changedCellSx("results")
																		: {}),
																}}
															>
																{isEditing ? (
																	<CustomInput
																		{...editor.valueField(
																			resultIndex,
																			column.field,
																		)}
																		disabled={isSaving}
																	/>
																) : updatedFrom !== undefined ? (
																	<ChangedValue previous={updatedFrom}>
																		{value}
																	</ChangedValue>
																) : (
																	value
																)}
															</Box>
														);
													})}
													<Box
														sx={{
															...cellSx,
															justifyContent: "center",
															...(overallFrom !== undefined
																? changedCellSx("results")
																: {}),
														}}
													>
														<ChangedValue previous={overallFrom}>
															<ResultChip passed={result.OverallPassed} />
														</ChangedValue>
													</Box>
												</Box>
											);
										})}
									</Box>
									<Box
										sx={{
											width: REPORTED_WIDTH,
											flexShrink: 0,
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											borderLeft: 1,
											borderColor: "divider",
											fontSize: 11,
											lineHeight: 1.4,
											whiteSpace: "nowrap",
											color: "text.secondary",
										}}
									>
										{formatTimestamp(shown.reported)}
									</Box>
								</Box>
							</>
						)}

						{renderLimits(shown)}

						{curves && (
							<Collapse
								in={openCurves.includes(group.runId)}
								unmountOnExit
								onEntered={(node: HTMLElement) =>
									node.scrollIntoView({ behavior: "smooth", block: "center" })
								}
							>
								<Box
									sx={{
										px: CARD_PX,
										pb: 1,
										borderTop: 1,
										borderColor: "divider",
									}}
								>
									<MechanicalCurvesChart
										heatId={curves.heatId}
										heatName={curves.heatName}
										results={results}
										runId={group.runId}
									/>
								</Box>
							</Collapse>
						)}
					</Box>
				);
			})}
		</Stack>
	);
};
